import { call, delay, put, select, race, take } from 'redux-saga/effects';
import {
  clearPendingNavigateAfterCreate,
  fetchOrganisationsFailure,
  fetchOrganisationsSuccess,
} from '../actions';
import { push } from 'store/navigation/actions';
import routes from 'routes';
import {
  FetchMappingsResponse,
  FetchOrganisationsResponse,
  getExcelFilesMetadata,
  getMappings,
  getOrganisations,
} from '../api';
import { ApiError } from 'store/api/types';
import {
  FetchOrganisationsRequestAction,
  FetchOrganisationsLightRequestAction,
} from '../types';
import moment from 'moment';
import { checkResponse } from 'store/utils';
import { Mapping, Organisation } from '../models';
import { MessageData, ProfilePicture } from 'store/app/types';
import { MessageStates } from 'containers/MessageBox';
import { turnMessageOn } from 'store/app/actions';
import { PICTURE_DELAY_TIME } from 'store/app/constants';
import { GetTaxRatesResponse, getTaxRates } from 'store/returns/api';
import { TaxRate } from 'store/returns/models/returnData';
import { saveTaxRatesSuccess, setGettingTaxes } from 'store/tax-rates/actions';
import { getProfilePictures } from 'store/app/selectors';
import { fetchProfilePictureRequest } from 'store/account/actions';
import { getAuthUser } from 'store/auth/selectors';
import { User } from 'store/auth/models';
import { Provider } from 'store/api/enums';
import { logoutRequest } from 'store/auth/actions';
import { renderErrorMessage } from 'utils/render-error-message';
import { entitySupportsDocumentScanning } from 'utils/captureTransactionsAccess';
import { getUserMembers } from 'store/user/selectors';
import { fetchUsersRequest } from 'store/user/actions';
import { FETCH_USERS_SUCCESS, FETCH_USERS_FAILURE } from 'store/user/constants';
import i18n from 'i18next';

interface SavedRateEntry {
  connectionId: string;
  taxRates: TaxRate[];
  taxRatesUnique?: TaxRate[];
}

interface SavedRates {
  userUuid: string;
  timestamp: string;
  rates: SavedRateEntry[];
}

const isQuickBooksOrganisation = (org: Pick<Organisation, 'providerName'>) =>
  org.providerName?.toLowerCase() === Provider.QUICKBOOKS.toLowerCase();

const copyTaxRates = (taxRates: TaxRate[] | undefined): TaxRate[] =>
  taxRates ? [...taxRates] : [];

const parseGetTaxRatesResponse = (
  settled: PromiseSettledResult<GetTaxRatesResponse>
): GetTaxRatesResponse | null => {
  if (settled.status !== 'fulfilled' || !settled.value) return null;
  const inner: any = (settled.value as any)?.data ?? settled.value;
  if (!inner?.connectionId) return null;
  return inner as GetTaxRatesResponse;
};

/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* fetchOrganisationsSaga(
  action: FetchOrganisationsRequestAction
) {
  const searchPrompt = action.payload?.searchPrompt || '';
  const REFRESH_TIMEOUT = 0;
  let currentUser: User = yield select(getAuthUser);
  if (!currentUser) {
    do {
      delay(500);
      currentUser = yield select(getAuthUser);
    } while (!currentUser);
  }
  const fileName = `gen-tax-tax-rates-${currentUser.userUuid}`;

  try {
    let errOrgs: ApiError | null;
    //let errMaps: ApiError | null;
    // Ensure members are loaded before calling getOrganisations
    let members: User[] | undefined = yield select(getUserMembers);

    if (!members || members.length === 0) {
      yield put(fetchUsersRequest());

      const { success } = yield race({
        success: take(FETCH_USERS_SUCCESS),
        failure: take(FETCH_USERS_FAILURE),
      });

      if (success) {
        members = yield select(getUserMembers);
      }
    }

    const currentUsers: User[] = members || [];
    const responseOrgs: FetchOrganisationsResponse = yield call(
      getOrganisations,
      currentUsers,
      searchPrompt
    );

    const orgs: Organisation[] = responseOrgs.data.organisations;
    const maps: Mapping[] = responseOrgs.data.mappings;

    errOrgs = checkResponse(responseOrgs?.responseMessage);
    if (errOrgs) throw errOrgs;

    if (orgs && orgs.length === 0 && !currentUser.firstLogin) {
      const message: MessageData = {
        title: i18n.t('organisation.no-entities-found-title'),
        description: i18n.t('organisation.no-entities-found-description'),
        type: MessageStates.WARNING,
      };
      yield put(turnMessageOn(message));
    }
    // testing
    orgs.map(function (x) {
      x.connectedProperly = false;
      return x;
    });

    //const responseMaps: FetchMappingsResponse = yield call(getMappings);
    //const maps: Mapping[] = responseMaps.data.mappings;

    //errMaps = checkResponse(responseMaps?.responseMessage);
    //if (errMaps) throw errMaps;

    // prepare for taxes call and get from cache

    const allCalls: string[] = [];
    const savedRatesString = localStorage.getItem(fileName);

    const savedRates: SavedRates = savedRatesString
      ? JSON.parse(savedRatesString)
      : null;
    const rates = savedRates?.rates;
    const timestamp = savedRates?.timestamp
      ? moment(savedRates?.timestamp)
      : null;

    let newEntities = false;

    if (rates) {
      if (
        rates.length !==
        orgs.filter((o) => o.providerName !== Provider.CIN7).length
      )
        newEntities = true;
      else
        orgs.forEach((o) => {
          let found = false;
          rates.forEach((r) => {
            if (r.connectionId === o.connectionId) found = true;
          });
          newEntities = !found;
        });
    }
    if (!timestamp || newEntities) {
      localStorage.removeItem(fileName);
    }

    for (let i = 0; i < orgs.length; i++) {
      if (orgs[i].providerName === Provider.HMRC) continue;
      const connId = orgs[i].connectionId;

      allCalls.push(connId);
      if (rates) {
        const rate = rates?.find((r) => r.connectionId === connId);
        if (rate) {
          const taxRates = rate.taxRates;
          orgs[i].taxRates = taxRates;
          if (isQuickBooksOrganisation(orgs[i])) {
            if (rate.taxRatesUnique !== undefined) {
              orgs[i].taxRatesUnique = rate.taxRatesUnique;
            }
          } else {
            orgs[i].taxRatesUnique = copyTaxRates(taxRates);
          }
          orgs[i].connectedProperly = true;
          if (taxRates && taxRates.length > 0) {
            yield put(saveTaxRatesSuccess(taxRates, connId));
            orgs[i].hasTaxRates = true;
          } else {
            orgs[i].hasTaxRates = false;
          }
        }
      }
    }

    // Members are already loaded at the start of the saga

    const findUserByAccountId = (
      accountId: string | null
    ): User | undefined => {
      if (!accountId || !members) return undefined;
      return members.find((m) => m.accountUuid === accountId);
    };

    const findUserByEmailOrAccountId = (
      identifier: string | null | undefined
    ): User | undefined => {
      if (!identifier || !members || identifier.trim() === '') return undefined;
      const identifierTrimmed = identifier.trim();
      // Try to find by accountUuid first
      const byAccountId = members.find(
        (m) => m.accountUuid === identifierTrimmed
      );
      if (byAccountId) return byAccountId;
      // If not found, try to find by email (case-insensitive)
      return members.find(
        (m) =>
          m.email && m.email.toLowerCase() === identifierTrimmed.toLowerCase()
      );
    };

    // Populate dataLog users from members
    for (let b = 0; b < maps.length; b++) {
      const dataLogs = maps[b].dataLogs || [];
      if (dataLogs.length > 0) {
        const updatedDataLogs = [...dataLogs];

        for (let d = 0; d < updatedDataLogs.length; d++) {
          let dataLog: any = { ...updatedDataLogs[d] };

          dataLog.preparerUser = findUserByAccountId(dataLog.savedByAccountId);
          dataLog.reviewerUser = findUserByAccountId(dataLog.reviewerAccountId);
          dataLog.clientUser = findUserByAccountId(dataLog.clientAccountId);
          dataLog.submittedByUser = findUserByEmailOrAccountId(
            dataLog.submittedBy
          );

          updatedDataLogs[d] = dataLog;
        }

        maps[b] = { ...maps[b], dataLogs: updatedDataLogs };
      }
    }

    yield put(fetchOrganisationsSuccess(orgs, maps));

    // Fetch file metadata for capture entities (background, does not block initial load).
    const metadataOrgs = orgs.filter((org) => {
      if (org.isGroup) return false;
      const provider = org.providerName?.toLowerCase();
      if (provider === Provider.EXCEL.toLowerCase()) return true;
      return (
        entitySupportsDocumentScanning(org.providerName) &&
        org.receiptScanStepsCompleted === true
      );
    });
    if (metadataOrgs.length > 0) {
      const results: PromiseSettledResult<any>[] = yield call(() =>
        Promise.allSettled(
          metadataOrgs.map((org) => getExcelFilesMetadata(org.uuid))
        )
      );
      results.forEach((result, i) => {
        if (
          result.status === 'fulfilled' &&
          result.value?.data?.files !== undefined
        ) {
          metadataOrgs[i].excelFilesMetadata = result.value.data.files;
        }
      });
    }

    let pp: ProfilePicture[] = yield select(getProfilePictures);
    const requestedUserUuids = new Set<string>();

    for (let a = 0; a < orgs.length; a++) {
      let users = [...orgs[a].users];
      for (let i = 0; i < orgs[a].users.length; i++) {
        const user = { ...orgs[a].users[i] };
        if (user && user.hasProfilePicture && !user.profilePicture) {
          const ppIdx = pp.findIndex((p) => p.userUuid === user.userUuid);
          if (ppIdx > -1) {
            const profilePicture = pp[ppIdx].profilePicture;
            users[i] = { ...users[i], profilePicture };
          } else if (!requestedUserUuids.has(user.userUuid)) {
            requestedUserUuids.add(user.userUuid);
            yield put(fetchProfilePictureRequest(user.userUuid));
            yield delay(PICTURE_DELAY_TIME);
            pp = yield select(getProfilePictures);

            const ppIdx = pp.findIndex((p) => p.userUuid === user.userUuid);
            if (ppIdx > -1) {
              const profilePicture = pp[ppIdx].profilePicture;
              users[i] = { ...users[i], profilePicture };
            }
          }
        }
      }
      orgs[a] = { ...orgs[a], users };
    }

    // Fetch profile pictures for dataLog users
    const activityUsers = [
      'preparerUser',
      'reviewerUser',
      'clientUser',
      'submittedByUser',
    ];

    for (let b = 0; b < maps.length; b++) {
      const dataLogs = maps[b].dataLogs || [];
      if (dataLogs.length > 0) {
        const updatedDataLogs = [...dataLogs];

        for (let d = 0; d < updatedDataLogs.length; d++) {
          let dataLog: any = { ...updatedDataLogs[d] };

          for (let c = 0; c < activityUsers.length; c++) {
            //@ts-ignore
            const user = { ...dataLog[activityUsers[c]] };
            if (user) {
              if (user.hasProfilePicture && !user.profilePicture) {
                const ppIdx = pp.findIndex((p) => p.userUuid === user.userUuid);
                if (ppIdx > -1) {
                  const profilePicture = pp[ppIdx].profilePicture;
                  if (dataLog[activityUsers[c]]) {
                    dataLog[activityUsers[c]] = {
                      ...dataLog[activityUsers[c]],
                      profilePicture,
                    };
                  }
                  updatedDataLogs[d] = dataLog;
                } else if (!requestedUserUuids.has(user.userUuid)) {
                  requestedUserUuids.add(user.userUuid);
                  yield put(fetchProfilePictureRequest(user.userUuid));
                  yield delay(PICTURE_DELAY_TIME);
                  pp = yield select(getProfilePictures);
                  const ppIdx = pp.findIndex(
                    (p) => p.userUuid === user.userUuid
                  );
                  if (ppIdx > -1) {
                    const profilePicture = pp[ppIdx].profilePicture;
                    if (dataLog[activityUsers[c]]) {
                      dataLog[activityUsers[c]] = {
                        ...dataLog[activityUsers[c]],
                        profilePicture,
                      };
                    }
                    updatedDataLogs[d] = dataLog;
                  }
                }
              }
            }
          }
        }

        maps[b] = { ...maps[b], dataLogs: updatedDataLogs };
      }
    }
    ////TAXES
    const timeToRefresh = timestamp
      ? moment().isAfter(timestamp.add(REFRESH_TIMEOUT, 'minutes'))
      : true;

    if (timeToRefresh || newEntities) {
      yield put(setGettingTaxes(true));
      let newTaxes: GetTaxRatesResponse[] = [];
      const taxResponses = yield call(getTaxRatesPromises, allCalls);
      taxResponses.map((tr) => {
        if (tr.value?.data) newTaxes.push(tr.value.data);
      });

      const ratesToSave: SavedRateEntry[] = [];
      for (let i = 0; i < orgs.length; i++) {
        const curTaxRates = newTaxes.find(
          (nt) => !!nt && nt.connectionId === orgs[i].connectionId
        );

        if (curTaxRates) {
          const taxRates: TaxRate[] = curTaxRates.taxRates;
          orgs[i].taxRates = taxRates;
          orgs[i].taxEnabled = curTaxRates.taxEnabled;
          orgs[i].connectedProperly = true;
          if (!isQuickBooksOrganisation(orgs[i])) {
            orgs[i].taxRatesUnique = copyTaxRates(taxRates);
          }
          ratesToSave.push({ connectionId: orgs[i].connectionId, taxRates });
          if (taxRates && taxRates.length > 0) {
            yield put(saveTaxRatesSuccess(taxRates, orgs[i].connectionId));
            orgs[i].hasTaxRates = true;
          } else {
            orgs[i].hasTaxRates = false;
          }
        } else {
          orgs[i].taxRates = [];
          orgs[i].taxRatesUnique = [];
          orgs[i].taxEnabled = false;
          orgs[i].connectedProperly = false;
          orgs[i].hasTaxRates = false;
        }
      }

      const quickbooksConnIds = orgs
        .filter((o) => isQuickBooksOrganisation(o))
        .map((o) => o.connectionId);
      if (quickbooksConnIds.length > 0) {
        const uniqueResponses: PromiseSettledResult<GetTaxRatesResponse>[] =
          yield call(getTaxRatesUniquePromises, quickbooksConnIds);
        uniqueResponses.forEach((settled) => {
          const payload = parseGetTaxRatesResponse(settled);
          if (!payload) return;
          const org = orgs.find((o) => o.connectionId === payload.connectionId);
          if (!org) return;
          const taxRatesUnique = payload.taxRates ?? [];
          org.taxRatesUnique = taxRatesUnique;
          const saved = ratesToSave.find(
            (r) => r.connectionId === payload.connectionId
          );
          if (saved) saved.taxRatesUnique = taxRatesUnique;
        });
      }

      ratesToSave.length > 0
        ? localStorage.setItem(
            fileName,
            JSON.stringify({ timestamp: moment(), rates: ratesToSave })
          )
        : localStorage.removeItem(fileName);

      yield put(setGettingTaxes(false));
    } else {
      for (let i = 0; i < orgs.length; i++) {
        if (orgs[i].providerName === Provider.HMRC) continue;
        if (!isQuickBooksOrganisation(orgs[i]) && orgs[i].taxRates) {
          orgs[i].taxRatesUnique = copyTaxRates(orgs[i].taxRates);
        }
      }
    }

    const quickbooksNeedingUnique = orgs
      .filter((o) => isQuickBooksOrganisation(o) && o.connectedProperly)
      .filter((o) => o.taxRatesUnique === undefined)
      .map((o) => o.connectionId);
    if (quickbooksNeedingUnique.length > 0) {
      const uniqueResponses: PromiseSettledResult<GetTaxRatesResponse>[] =
        yield call(getTaxRatesUniquePromises, quickbooksNeedingUnique);
      uniqueResponses.forEach((settled) => {
        const payload = parseGetTaxRatesResponse(settled);
        if (!payload) return;
        const org = orgs.find((o) => o.connectionId === payload.connectionId);
        if (!org) return;
        org.taxRatesUnique = payload.taxRates ?? [];
      });
    }

    const pendingNavigate:
      | { legalName: string; providerName: string }
      | undefined = yield select(
      (state: {
        organisation: {
          pendingNavigateAfterCreate?: {
            legalName: string;
            providerName: string;
          };
        };
      }) => state.organisation.pendingNavigateAfterCreate
    );
    if (
      pendingNavigate &&
      (pendingNavigate.providerName === Provider.EXCEL ||
        pendingNavigate.providerName === Provider.RECEIPTS)
    ) {
      const newOrg = orgs?.find(
        (o) =>
          o.legalName === pendingNavigate.legalName &&
          o.providerName === pendingNavigate.providerName
      );
      if (newOrg) {
        yield put(
          push(
            `${routes.organisations.organisation.replace(
              ':id',
              newOrg.uuid
            )}?tab=capture`
          )
        );
        yield put(clearPendingNavigateAfterCreate());
      }
    }

    yield put(fetchOrganisationsSuccess(orgs, maps));
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
    yield put(fetchOrganisationsFailure());
  }
}

// Lightweight organisations refresh used after creating an entity.
// This avoids the tax rates, Excel metadata, and profile picture fan-out
// performed by the full fetchOrganisationsSaga, while still refreshing the
// organisations/mappings lists.
/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* fetchOrganisationsLightSaga(
  action: FetchOrganisationsLightRequestAction
) {
  const searchPrompt = action.payload?.searchPrompt || '';
  try {
    let currentUser: User = yield select(getAuthUser);
    if (!currentUser) {
      do {
        // Wait briefly for auth user to be available
        yield delay(500);
        currentUser = yield select(getAuthUser);
      } while (!currentUser);
    }

    let members: User[] | undefined = yield select(getUserMembers);

    if (!members || members.length === 0) {
      yield put(fetchUsersRequest());

      const { success } = yield race({
        success: take(FETCH_USERS_SUCCESS),
        failure: take(FETCH_USERS_FAILURE),
      });

      if (success) {
        members = yield select(getUserMembers);
      }
    }

    const currentUsers: User[] = members || [];
    const responseOrgs: FetchOrganisationsResponse = yield call(
      getOrganisations,
      currentUsers,
      searchPrompt
    );
    const orgs: Organisation[] = responseOrgs.data.organisations;
    const maps: Mapping[] = responseOrgs.data.mappings;

    const errOrgs: ApiError | null = checkResponse(
      responseOrgs?.responseMessage
    );
    if (errOrgs) throw errOrgs;

    if (orgs && orgs.length === 0 && !currentUser.firstLogin) {
      const message: MessageData = {
        title: i18n.t('organisation.no-entities-found-title'),
        description: i18n.t('organisation.no-entities-found-description'),
        type: MessageStates.WARNING,
      };
      yield put(turnMessageOn(message));
    }

    yield put(fetchOrganisationsSuccess(orgs, maps));
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
    yield put(fetchOrganisationsFailure());
  }
}

const getTaxRatesPromises = async (list: string[]) => {
  return await Promise.allSettled(list.map((l) => getTaxRates(l)));
};

const getTaxRatesUniquePromises = async (list: string[]) => {
  return await Promise.allSettled(
    list.map((l) => getTaxRates(l, { separatePurchasesAndSales: false }))
  );
};
